import type { Call } from '@bcio-voice/contracts';
import { useRef, useState } from 'react';
import { PhoneOutgoing } from 'lucide-react';
import { useWorkspace } from '../../state/workspace.tsx';
import { useRouter } from '../../lib/router.tsx';
import { Icon } from '../../ui/Icon.tsx';
import { partyName } from '../phone/callView.ts';
import { callbackDncReason, callbackPlan, openCallback } from './callback.ts';

export function CallbackButton({ call, detail = false }: { call: Call; detail?: boolean }) {
  const ws = useWorkspace();
  const { navigate } = useRouter();
  const latest = useRef(ws);
  latest.current = ws;
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const locked = useRef(false);
  const plan = callbackPlan(call, ws);
  if (!plan.visible) return null;
  const label = call.direction === 'outbound' ? 'Call again' : 'Call back';
  const reason = error ?? plan.reason;
  const click = async () => {
    if (locked.current || !plan.draft) return;
    locked.current = true;
    setError(null);
    try {
      setChecking(true);
      const dncReason = await callbackDncReason(plan.draft, ws);
      if (dncReason) { setError(dncReason); return; }
      setError(openCallback(call, latest.current, navigate));
    } catch {
      setError('Could not check the do-not-call list. Try again.');
    } finally {
      locked.current = false;
      setChecking(false);
    }
  };
  return <span className="call-callback" style={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
    <button type="button" className={`btn ${detail ? 'btn-live' : 'btn-small'}`} disabled={!plan.draft || checking}
      onClick={() => void click()} aria-busy={checking || undefined}
      aria-label={`${label} ${partyName(call, ws.userId, ws.userName)}`}
      title={reason ?? 'Open the phone with this number ready. Press Call to start.'}>
      <Icon icon={PhoneOutgoing} /> {checking ? 'Checking...' : label}
    </button>
    {error || (detail && reason) ? <span className="field-error" role={error ? 'alert' : undefined}>{reason}</span> : null}
    {detail && plan.draft && !error ? <span className="field-hint">Opens the phone; press Call to start.</span> : null}
  </span>;
}
